import React, { useState } from 'react';
import { DownloadSimple, Paperclip } from '@phosphor-icons/react';
import AsyncState from '../../../components/AsyncState';
import { useLanguage } from '../../../contexts/LanguageContext';
import { translations } from '../../../translations';
import { describeApiError, formatDateTime } from '../../../utils/format';
import { requestJson } from '../../../utils/lifecycle';
import { downloadAsset } from './AssetsTab';

export default function DiscussionTab({ appId, comments, canWrite, onChanged }) {
  const { language } = useLanguage();
  const t = translations[language];
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState({ kind: '', text: '' });
  const items = comments.data?.items || [];

  const send = async (event) => {
    event.preventDefault();
    if (!message.trim()) return;
    setBusy(true);
    setFeedback({ kind: '', text: '' });
    try {
      let attachmentIds = [];
      if (file) {
        const body = new FormData();
        body.append('file', file);
        body.append('category', 'other');
        const uploaded = await requestJson(`/custom-apps/${appId}/assets`, { method: 'POST', body });
        attachmentIds = [uploaded.id];
      }
      await requestJson(`/custom-apps/${appId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: message.trim(), attachmentIds }),
      });
      setMessage('');
      setFile(null);
      event.target.reset();
      setFeedback({ kind: 'ok', text: t.commentSent });
      await onChanged();
    } catch (error) {
      if (error.status !== 401) setFeedback({ kind: 'error', text: describeApiError(error, t, error.message) });
    } finally {
      setBusy(false);
    }
  };

  const download = async (attachment) => {
    try {
      await downloadAsset(appId, attachment);
    } catch (error) {
      if (error.status !== 401) setFeedback({ kind: 'error', text: describeApiError(error, t, error.message) });
    }
  };

  return <section className="card stack">
    <AsyncState state={comments} retry={comments.retry} isEmpty={items.length === 0} empty={<p className="hint">{t.noMessages}</p>}>
      <ul>
        {items.map((item) => <li className="comment" key={item.id}>
          <div className="comment__head"><strong>{item.actor?.name || t.teamMember}</strong><small>{item.role}</small><small>{formatDateTime(item.createdAt, language)}</small></div>
          <p>{item.message}</p>
          {(item.attachments || []).length > 0 && <ul aria-label={t.attachments}>
            {item.attachments.map((attachment) => <li key={attachment.id}>
              <button className="btn btn--pill" type="button" onClick={() => download(attachment)}>
                <DownloadSimple size={16} aria-hidden="true" /> {attachment.filename}
              </button>
            </li>)}
          </ul>}
        </li>)}
      </ul>
    </AsyncState>
    {canWrite && <form className="tile stack" onSubmit={send}>
      <div className="field">
        <label className="label" htmlFor="comment-message">{t.writeUpdate}</label>
        <textarea id="comment-message" className="textarea" rows="3" value={message} onChange={(event) => setMessage(event.target.value)} maxLength={4000} />
      </div>
      <div className="field">
        <label className="label" htmlFor="comment-file"><Paperclip size={16} aria-hidden="true" /> {t.attachFile} <small>({t.optional})</small></label>
        <input id="comment-file" type="file" onChange={(event) => setFile(event.target.files[0] || null)} />
      </div>
      <div className="btn-row"><button className="btn btn--primary" type="submit" disabled={!message.trim() || busy}>{t.sendUpdate}</button></div>
    </form>}
    {feedback.text && <p className={feedback.kind === 'error' ? 'notice notice--error' : 'hint'} role={feedback.kind === 'error' ? 'alert' : 'status'}>{feedback.text}</p>}
  </section>;
}
